import React, { useState } from 'react'
import { toast } from 'react-hot-toast'
import axiosInstance from '../../api/axiosInstance'
import { useAuth } from '../../contexts/AuthContext'
import { Select, Textarea, Button } from '../ui'
import { FormField } from '../ui/Form'
import { getFieldIcon } from '../../utils/formIcons'
import { PaperAirplaneIcon, MagnifyingGlassIcon, HashtagIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import Resultados from './Resultados'

interface Resultado {
  orderId: string
  error?: string
}

const PICKING_STARTED_ERROR_CODE = '20000213'
const PICKING_STARTED_ERROR_MESSAGE =
  'El pedido debe estar en estado "asignado" o "incidencia" para poder cambiar a Picking Iniciado'

const PickingIniciado: React.FC = () => {
  const [orderIds, setOrderIds] = useState<string>('')
  const [exitosos, setExitosos] = useState<string[]>([])
  const [fallidos, setFallidos] = useState<Resultado[]>([])
  const [searchType, setSearchType] = useState<'tracking' | 'id'>('tracking')
  const [isLoading, setIsLoading] = useState(false)
  const { user } = useAuth()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const ids = orderIds
      .split(/[\n,]+/)
      .map((id) => id.trim())
      .filter((id) => id !== '')

    if (ids.length === 0) {
      toast.error('Por favor ingrese al menos un número de guía o ID de pedido', { position: 'top-center' })
      return
    }

    setIsLoading(true)
    try {
      const requestBody = {
        userId: user?.id,
        updatedBy: user
          ? {
              id: user.id,
              first_name: user.first_name,
              last_name: user.last_name,
              email: user.email
            }
          : undefined,
        orders: ids.map((id) => {
          if (searchType === 'tracking') {
            return {
              trackingNumber: id,
              searchType: 'tracking',
              status: 'picking_started'
            }
          }

          return {
            orderId: id,
            searchType: 'id',
            status: 'picking_started'
          }
        })
      }

      const response = await axiosInstance.patch('/status/update', requestBody)
      const data = response.data

      const exitososTransformados = Array.isArray(data.results?.success)
        ? data.results.success.map((item: any) => {
            if (typeof item === 'object' && item !== null) {
              return String(item[searchType === 'tracking' ? 'trackingNumber' : 'orderId'] || '')
            }
            return String(item)
          })
        : []

      const fallidosTransformados = Array.isArray(data.results?.failed)
        ? data.results.failed.map((item: any) => ({
            orderId: String(item[searchType === 'tracking' ? 'trackingNumber' : 'orderId'] || ''),
            error: (() => {
              const rawError = String(item.error || 'Error desconocido')
              if (
                rawError.includes(PICKING_STARTED_ERROR_CODE) ||
                rawError.toLowerCase().includes('piking_starterd') ||
                rawError.toLowerCase().includes('picking_started')
              ) {
                return `${PICKING_STARTED_ERROR_CODE}: ${PICKING_STARTED_ERROR_MESSAGE}`
              }
              return rawError
            })()
          }))
        : []

      setExitosos(exitososTransformados)
      setFallidos(fallidosTransformados)

      if (fallidosTransformados.length > 0) {
        toast.error('Algunos pedidos no se pudieron actualizar', { position: 'top-center' })
      } else {
        toast.success('Estados actualizados correctamente', { position: 'top-center' })
      }

      setOrderIds('')
    } catch (error: any) {
      const rawMessage = String(error?.response?.data?.message || error?.message || '')
      if (
        rawMessage.includes(PICKING_STARTED_ERROR_CODE) ||
        rawMessage.toLowerCase().includes('piking_starterd') ||
        rawMessage.toLowerCase().includes('picking_started')
      ) {
        toast.error(`${PICKING_STARTED_ERROR_CODE}: ${PICKING_STARTED_ERROR_MESSAGE}`, { position: 'top-center' })
      } else {
        toast.error('Error al actualizar los estados', { position: 'top-center' })
      }
      console.error('Error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-center mb-2">
            <PaperAirplaneIcon className="h-7 w-7 text-pink-600 mr-3" />
            <h2>Cambiar estado a Picking Iniciado</h2>
          </div>
          <p className="text-center text-gray-600 text-sm">
            Actualiza el estado de pedidos a "Picking Iniciado" usando números de guía o IDs de pedido
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <FormField
              label="Tipo de búsqueda"
              icon={MagnifyingGlassIcon}
            >
              <Select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value as 'tracking' | 'id')}
                options={[
                  { value: 'tracking', label: 'Número de guía' },
                  { value: 'id', label: 'ID de pedido' }
                ]}
              />
            </FormField>

            <FormField
              label={searchType === 'tracking' ? 'Números de guía' : 'IDs de pedidos'}
              helpText="Separe múltiples valores con comas o saltos de línea"
              icon={searchType === 'tracking' ? getFieldIcon('guia') : HashtagIcon}
            >
              <Textarea
                value={orderIds}
                onChange={(e) => setOrderIds(e.target.value)}
                rows={8}
                placeholder={searchType === 'tracking' ? 'ABC123\nABC124\nABC125' : '4823\n4822\n4821'}
              />
            </FormField>

            <Button
              type="submit"
              variant="primary"
              isLoading={isLoading}
              leftIcon={CheckCircleIcon}
              fullWidth
              size="lg"
            >
              {isLoading ? 'Actualizando...' : 'Actualizar estados'}
            </Button>
          </form>
        </div>

        <Resultados exitosos={exitosos} fallidos={fallidos} />
      </div>
    </div>
  )
}

export default PickingIniciado
